import React, { useState, useEffect, useContext } from 'react';
import RequestService from '../../services/RequestService';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import {
    Grid2 as Grid,
    Typography,
    Breadcrumbs,
    Link,
    TextField,
    Button,
    Box,
    Snackbar,
    Alert,
    useMediaQuery,
} from '@mui/material';
import { UserContext } from '../../contexts/UserContext';
import { useTheme } from '@mui/material/styles';
import GameDialog from '../../widgets/games/GameDialog';
import NewGameDialog from '../../widgets/games/NewGameDialog';
import GameCard from '../../widgets/games/GameCard';
import GameList from '../../widgets/games/GameList';
import GameDeleteDialog from '../../widgets/groups/GameDeleteDialog';

const GamePage = () => {
    const { user, hasPermission } = useContext(UserContext);
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedGame, setSelectedGame] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchGames = async () => {
            try {
                const data = await RequestService.getGames(user.company.id);
                setGames(data);
                setLoading(false);
            } catch (err) {
                setError('Erro ao carregar grupos.');
                setLoading(false);
            }
        };

        fetchGames();
    }, []);

    const handleRowClick = (game) => {
        if (hasPermission('GAME_UPDATE')) {
            setSelectedGame(game);
            setIsDialogOpen(true);
        }
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedGame(null);
    };

    const handleOpenAddDialog = () => setIsAddDialogOpen(true);
    const handleCloseAddDialog = () => setIsAddDialogOpen(false);

    const handleEdit = (game) => {
        handleRowClick(game);
    };

    const handleOpenDeleteDialog = (game) => {
        setSelectedGame(game);
        setIsDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setIsDeleteDialogOpen(false);
        setSelectedGame(null);
    };

    const handleSuccessDelete = async () => {
        const data = await RequestService.getGames(user.company.id);
        setGames(data);
        setSnackbarMessage('Jogo excluído com sucesso!');
        setSnackbarOpen(true);
    };

    const handleSuccessAdd = async (message) => {
        const data = await RequestService.getGames(user.company.id);
        setGames(data);
        setSnackbarMessage(message);
        setSnackbarOpen(true);
    };

    const handleSuccessEdit = async (message) => {
        const data = await RequestService.getGames(user.company.id);
        setGames(data);

        setSnackbarMessage(message);
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    if (loading) return <div>Carregando...</div>;
    if (error) return <div>{error}</div>;

    const renderEmptyState = () => (
        <Box
            sx={{
                textAlign: 'center',
                mt: 4,
                color: '#666',
                fontFamily: 'Poppins',
                fontSize: 18,
            }}
        >
            <Typography variant='h6'>Nenhum jogo cadastrado.</Typography>
            <Typography variant='body2'>
                Clique em <strong>"Novo jogo"</strong> para adicionar o
                primeiro!
            </Typography>
        </Box>
    );

    const renderMobileLayout = () => {
        return games.length === 0 ? (
            renderEmptyState()
        ) : (
            <GameList
                games={games}
                shouldEdit={hasPermission('GAME_UPDATE')}
                shouldDelete={hasPermission('GAME_DELETE')}
                onUserClick={handleRowClick}
                onDeleteClick={handleOpenDeleteDialog}
            />
        );
    };

    const renderDesktopLayout = () => {
        return games.length === 0 ? (
            renderEmptyState()
        ) : (
            <Grid container spacing={3} sx={{ mt: 2 }}>
                {games.map((game, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                        <GameCard
                            game={game}
                            shouldEdit={hasPermission('GAME_UPDATE')}
                            shouldDelete={hasPermission('GAME_DELETE')}
                            onEdit={handleEdit}
                            onDelete={() => handleOpenDeleteDialog(game)}
                        />
                    </Grid>
                ))}
            </Grid>
        );
    };

    return (
        <Box>
            <Grid container justifyContent='start'>
                <Breadcrumbs aria-label='breadcrumb'>
                    <Link underline='hover' color='inherit' href='/'>
                        Início
                    </Link>
                    <Link underline='hover' color='inherit'>
                        Definições
                    </Link>
                    <Typography sx={{ color: 'text.primary' }}>
                        Jogos
                    </Typography>
                </Breadcrumbs>
            </Grid>

            <Grid container xs={12} sx={{ mt: 3 }}>
                <Typography variant='h4' gutterBottom>
                    Jogos
                </Typography>
            </Grid>

            <Grid container sx={12} justifyContent={'end'}>
                <Grid item sx={6}>
                    {hasPermission('GAME_CREATE') && (
                        <Button
                            variant='contained'
                            sx={{ minHeight: '47px' }}
                            endIcon={<AddCircleOutlineOutlinedIcon />}
                            onClick={handleOpenAddDialog}
                        >
                            Novo Jogo
                        </Button>
                    )}
                </Grid>
            </Grid>

            {isMobile ? renderMobileLayout() : renderDesktopLayout()}

            <GameDialog
                open={isDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDialog();
                    }
                }}
                game={selectedGame}
                onSuccess={handleSuccessEdit}
                user={user}
            />

            <NewGameDialog
                open={isAddDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAddDialog();
                    }
                }}
                onSuccess={handleSuccessAdd}
                user={user}
            />

            <GameDeleteDialog
                open={isDeleteDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDeleteDialog();
                    }
                }}
                game={selectedGame}
                onSuccess={handleSuccessDelete}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity='success'
                    variant='filled'
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default GamePage;
